import React, { useMemo, useState } from 'react';
import {
    ALARM_DURATION_HOURS,
    computeBatteryLoad,
    emptyBatteryUnit,
    normalizeBatteryLoadSheet,
    type AlarmDurationKey,
    type BatteryChemistry,
    type BatteryLoadSheet,
    type BatteryUnitRecord,
    type PowerProvidedBy
} from '../../lib/battery-load-calc';
import { apiRequest } from '../../api/client';

export type { BatteryLoadSheet };

type Props = {
    inspectionId: string;
    initial?: BatteryLoadSheet | Record<string, unknown> | null;
    disabled?: boolean;
    onSaved: (sheet: BatteryLoadSheet, inspection: unknown) => void;
    onError: (message: string) => void;
};

const DURATION_OPTIONS: { key: AlarmDurationKey; label: string }[] = [
    { key: '5min', label: '5 minutes' },
    { key: '30min', label: '30 minutes' },
    { key: '1h', label: '1 hour' },
    { key: '2h', label: '2 hours' }
];

const POWER_OPTIONS: { key: PowerProvidedBy; label: string }[] = [
    { key: 'batteries', label: 'Batteries' },
    { key: 'generator', label: 'Generator' },
    { key: 'ups', label: 'UPS' },
    { key: 'combination', label: 'Combination' }
];

const CHEMISTRY_OPTIONS: { key: BatteryChemistry; label: string }[] = [
    { key: 'sla', label: 'Sealed Lead Acid' },
    { key: 'nicad', label: 'Ni-Cad' },
    { key: 'lithium', label: 'Lithium-Ion' },
    { key: 'wet_lead', label: 'Wet Lead' }
];

function numOr(v: string, fallback = 0): number {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
}

function strOr(n: number | undefined): string {
    return n != null && Number.isFinite(n) && n !== 0 ? String(n) : '';
}

type UnitFormProps = {
    unit: BatteryUnitRecord;
    index: number;
    sharedDuration: AlarmDurationKey;
    disabled?: boolean;
    onChange: (unit: BatteryUnitRecord) => void;
};

function BatteryUnitForm({ unit, index, sharedDuration, disabled, onChange }: UnitFormProps): React.JSX.Element {
    const duration = unit.alarmDuration || sharedDuration;

    const inputs = useMemo(
        () => ({
            supervisoryCurrentMa: unit.supervisoryCurrentMa || unit.currentSupervisoryMa || 0,
            alarmCurrentA: unit.alarmCurrentA || unit.currentAlarmA || 0,
            alarmDuration: duration,
            deratingFactor: unit.deratingFactor || 1.25,
            installedBatteryAh: unit.installedBatteryAh || 0,
            installedBatteryQty: Math.max(1, unit.installedBatteryQty || 2),
            ratedVoltage: unit.ratedVoltage || 12,
            voltageAfterTest: unit.voltageAfterTest
        }),
        [unit, duration]
    );

    const result = useMemo(() => computeBatteryLoad(inputs), [inputs]);
    const canCalc = inputs.supervisoryCurrentMa > 0 && inputs.alarmCurrentA > 0;

    function patch(partial: Partial<BatteryUnitRecord>) {
        onChange({ ...unit, ...partial });
    }

    return (
        <div className="ir-battery-unit">
            <label className="ir-battery-calc__field ir-battery-calc__field--wide">
                <span>Supply name</span>
                <input
                    value={unit.label}
                    onChange={(e) => patch({ label: e.target.value })}
                    disabled={disabled}
                    placeholder={`e.g. FACU internal, RPS-${index + 1}`}
                />
            </label>

            <div className="ir-battery-calc__grid">
                <label className="ir-battery-calc__field ir-battery-calc__field--wide">
                    <span>Field location (GA01)</span>
                    <input
                        value={unit.epsLocation || ''}
                        onChange={(e) => patch({ epsLocation: e.target.value })}
                        disabled={disabled}
                    />
                </label>
                <label className="ir-battery-calc__field ir-battery-calc__field--wide">
                    <span>Identification (GA02)</span>
                    <input
                        value={unit.epsIdentification || ''}
                        onChange={(e) => patch({ epsIdentification: e.target.value })}
                        disabled={disabled}
                    />
                </label>
                <label className="ir-battery-calc__field">
                    <span>Chemistry (GA04)</span>
                    <select
                        value={unit.batteryChemistry || 'sla'}
                        onChange={(e) => patch({ batteryChemistry: e.target.value as BatteryChemistry })}
                        disabled={disabled}
                    >
                        {CHEMISTRY_OPTIONS.map((o) => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                    </select>
                </label>
                <label className="ir-battery-calc__field">
                    <span>Batteries in bank (GA05)</span>
                    <input
                        type="number"
                        min={1}
                        value={String(unit.installedBatteryQty ?? 2)}
                        onChange={(e) => patch({ installedBatteryQty: Math.max(1, Math.round(numOr(e.target.value, 2))) })}
                        disabled={disabled}
                    />
                </label>
                <label className="ir-battery-calc__field">
                    <span>Voltage V dc (GA06)</span>
                    <select
                        value={String(unit.ratedVoltage || 12)}
                        onChange={(e) => patch({ ratedVoltage: numOr(e.target.value, 12) })}
                        disabled={disabled}
                    >
                        <option value="12">12 V</option>
                        <option value="24">24 V</option>
                    </select>
                </label>
                <label className="ir-battery-calc__field">
                    <span>Ah each (GA07)</span>
                    <input
                        type="number"
                        step="any"
                        value={strOr(unit.installedBatteryAh)}
                        onChange={(e) => patch({ installedBatteryAh: numOr(e.target.value) })}
                        disabled={disabled}
                    />
                </label>
                <label className="ir-battery-calc__field">
                    <span>Date code (G18)</span>
                    <input
                        value={unit.manufacturerDateCode || ''}
                        onChange={(e) => patch({ manufacturerDateCode: e.target.value })}
                        disabled={disabled}
                    />
                </label>
            </div>

            <p className="ir-battery-calc__section-label">Measured — C, D, E</p>
            <div className="ir-battery-calc__grid">
                <label className="ir-battery-calc__field">
                    <span>C — Main ON (V)</span>
                    <input type="number" step="any" value={strOr(unit.voltageMainOn)} onChange={(e) => patch({ voltageMainOn: numOr(e.target.value) })} disabled={disabled} />
                </label>
                <label className="ir-battery-calc__field">
                    <span>C — Charge (mA)</span>
                    <input type="number" step="any" value={strOr(unit.chargingCurrentMa)} onChange={(e) => patch({ chargingCurrentMa: numOr(e.target.value) })} disabled={disabled} />
                </label>
                <label className="ir-battery-calc__field">
                    <span>D — Supervisory (V)</span>
                    <input type="number" step="any" value={strOr(unit.voltageSupervisoryOff)} onChange={(e) => patch({ voltageSupervisoryOff: numOr(e.target.value) })} disabled={disabled} />
                </label>
                <label className="ir-battery-calc__field">
                    <span>D — Supervisory (mA)</span>
                    <input type="number" step="any" value={strOr(unit.currentSupervisoryMa)} onChange={(e) => patch({ currentSupervisoryMa: numOr(e.target.value), supervisoryCurrentMa: numOr(e.target.value) })} disabled={disabled} />
                </label>
                <label className="ir-battery-calc__field">
                    <span>E — Alarm (V)</span>
                    <input type="number" step="any" value={strOr(unit.voltageAlarmOff)} onChange={(e) => patch({ voltageAlarmOff: numOr(e.target.value) })} disabled={disabled} />
                </label>
                <label className="ir-battery-calc__field">
                    <span>E — Alarm (A)</span>
                    <input type="number" step="any" value={strOr(unit.currentAlarmA)} onChange={(e) => patch({ currentAlarmA: numOr(e.target.value), alarmCurrentA: numOr(e.target.value) })} disabled={disabled} />
                </label>
            </div>

            <p className="ir-battery-calc__section-label">Calculation</p>
            <div className="ir-battery-calc__grid">
                <label className="ir-battery-calc__field">
                    <span>Supervisory calc (mA)</span>
                    <input type="number" step="any" value={strOr(unit.supervisoryCurrentMa)} onChange={(e) => patch({ supervisoryCurrentMa: numOr(e.target.value) })} disabled={disabled} placeholder="Uses D if blank" />
                </label>
                <label className="ir-battery-calc__field">
                    <span>Alarm calc (A)</span>
                    <input type="number" step="any" value={strOr(unit.alarmCurrentA)} onChange={(e) => patch({ alarmCurrentA: numOr(e.target.value) })} disabled={disabled} placeholder="Uses E if blank" />
                </label>
                <label className="ir-battery-calc__field">
                    <span>Derating</span>
                    <input
                        type="number"
                        step="0.05"
                        value={String(unit.deratingFactor ?? 1.25)}
                        onChange={(e) => patch({ deratingFactor: numOr(e.target.value, 1.25) })}
                        disabled={disabled}
                    />
                </label>
                <label className="ir-battery-calc__field">
                    <span>Q — After test (V)</span>
                    <input type="number" step="any" value={strOr(unit.voltageAfterTest)} onChange={(e) => patch({ voltageAfterTest: numOr(e.target.value) })} disabled={disabled} />
                </label>
                <label className="ir-battery-calc__field">
                    <span>S — Charge (A)</span>
                    <input type="number" step="any" value={strOr(unit.chargingCurrentA)} onChange={(e) => patch({ chargingCurrentA: numOr(e.target.value) })} disabled={disabled} />
                </label>
            </div>

            {canCalc ? (
                <div className="ir-battery-calc__results">
                    <div className="ir-battery-calc__result-row ir-battery-calc__result-row--total">
                        <span>P — Required</span>
                        <strong>{result.requiredAh} Ah</strong>
                    </div>
                    {result.ratingMeetsLoad != null ? (
                        <p className={result.ratingMeetsLoad ? 'ir-battery-calc__ok' : 'ir-battery-calc__warn'}>
                            {result.ratingMeetsLoad ? `Installed ${result.installedTotalAh} Ah OK` : `Installed ${result.installedTotalAh} Ah below required`}
                        </p>
                    ) : null}
                    {result.voltage85Ok != null ? (
                        <p className={result.voltage85Ok ? 'ir-battery-calc__ok' : 'ir-battery-calc__warn'}>
                            {result.voltage85Ok ? '85% voltage OK' : 'Below 85% rated voltage'}
                        </p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export default function BatteryLoadCalculator(props: Props): React.JSX.Element {
    const { inspectionId, initial, disabled, onSaved, onError } = props;
    const normalized = useMemo(() => normalizeBatteryLoadSheet(initial), [initial]);

    const [powerBy, setPowerBy] = useState<PowerProvidedBy>(normalized.powerProvidedBy || 'batteries');
    const [sharedDuration, setSharedDuration] = useState<AlarmDurationKey>(normalized.alarmDuration || '5min');
    const [units, setUnits] = useState<BatteryUnitRecord[]>(normalized.units);
    const [activeId, setActiveId] = useState(normalized.units[0]?.id || '');
    const [saving, setSaving] = useState(false);

    const activeIndex = Math.max(0, units.findIndex((u) => u.id === activeId));
    const activeUnit = units[activeIndex] || units[0];

    function addUnit() {
        const next = emptyBatteryUnit(units.length + 1, sharedDuration);
        setUnits((prev) => [...prev, next]);
        setActiveId(next.id);
    }

    function removeUnit(id: string) {
        if (units.length <= 1) return;
        const next = units.filter((u) => u.id !== id);
        setUnits(next);
        if (activeId === id) setActiveId(next[0]?.id || '');
    }

    function updateUnit(unit: BatteryUnitRecord) {
        setUnits((prev) => prev.map((u) => (u.id === unit.id ? unit : u)));
    }

    async function save() {
        setSaving(true);
        try {
            const preparedUnits = units.map((unit) => {
                const duration = unit.alarmDuration || sharedDuration;
                const inputs = {
                    supervisoryCurrentMa: unit.supervisoryCurrentMa || unit.currentSupervisoryMa || 0,
                    alarmCurrentA: unit.alarmCurrentA || unit.currentAlarmA || 0,
                    alarmDuration: duration,
                    deratingFactor: unit.deratingFactor || 1.25,
                    installedBatteryAh: unit.installedBatteryAh || 0,
                    installedBatteryQty: Math.max(1, unit.installedBatteryQty || 2),
                    ratedVoltage: unit.ratedVoltage || 12,
                    voltageAfterTest: unit.voltageAfterTest
                };
                const calc =
                    inputs.supervisoryCurrentMa > 0 && inputs.alarmCurrentA > 0
                        ? computeBatteryLoad(inputs)
                        : null;
                return {
                    ...unit,
                    alarmDuration: duration,
                    supervisoryCurrentMa: inputs.supervisoryCurrentMa,
                    alarmCurrentA: inputs.alarmCurrentA,
                    requiredAh: calc?.requiredAh,
                    summaryNote: calc?.summaryNote
                };
            });

            const sheet: BatteryLoadSheet = {
                powerProvidedBy: powerBy,
                alarmDuration: sharedDuration,
                units: preparedUnits
            };

            const res = await apiRequest<{ success: boolean; data: unknown }>(
                `/api/inspections/${inspectionId}/annual-sheets`,
                { method: 'PATCH', json: { sheets: { batteryLoad: sheet } } }
            );
            onSaved(sheet, res.data);
        } catch (e: unknown) {
            onError(e instanceof Error ? e.message : 'Could not save power supply sheet.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="ir-battery-calc">
            <h3 className="ir-battery-calc__title">§22.5 Emergency Power Supply</h3>
            <p className="ir-battery-calc__hint">
                Add one worksheet per emergency power supply (FACU, remote power supply, booster, etc.). Each bank gets its own Annex C2 calculation.
            </p>

            <div className="ir-battery-calc__grid">
                <label className="ir-battery-calc__field">
                    <span>Provided by (GA03)</span>
                    <select value={powerBy} onChange={(e) => setPowerBy(e.target.value as PowerProvidedBy)} disabled={disabled}>
                        {POWER_OPTIONS.map((o) => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                    </select>
                </label>
                <label className="ir-battery-calc__field">
                    <span>NBC alarm time (GA08)</span>
                    <select value={sharedDuration} onChange={(e) => setSharedDuration(e.target.value as AlarmDurationKey)} disabled={disabled}>
                        {DURATION_OPTIONS.map((o) => (
                            <option key={o.key} value={o.key}>{o.label} ({ALARM_DURATION_HOURS[o.key]} h)</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="ir-battery-tabs">
                {units.map((u, i) => (
                    <button
                        key={u.id}
                        type="button"
                        className={`ir-battery-tabs__btn${u.id === activeUnit?.id ? ' ir-battery-tabs__btn--active' : ''}`}
                        onClick={() => setActiveId(u.id)}
                    >
                        {u.label || `Supply ${i + 1}`}
                    </button>
                ))}
                <button type="button" className="ir-battery-tabs__add" disabled={disabled} onClick={addUnit}>
                    + Add supply
                </button>
            </div>

            {activeUnit ? (
                <BatteryUnitForm
                    unit={activeUnit}
                    index={activeIndex}
                    sharedDuration={sharedDuration}
                    disabled={disabled}
                    onChange={updateUnit}
                />
            ) : null}

            {units.length > 1 ? (
                <button
                    type="button"
                    className="ir-battery-calc__remove"
                    disabled={disabled}
                    onClick={() => removeUnit(activeUnit.id)}
                >
                    Remove this supply
                </button>
            ) : null}

            <button type="button" className="ir-battery-calc__save" disabled={saving || disabled} onClick={() => void save()}>
                {saving ? 'Saving…' : `Save ${units.length} supply worksheet${units.length > 1 ? 's' : ''}`}
            </button>
        </div>
    );
}
